import { useBrand } from '@/hooks/use-brand';
import { router } from '@inertiajs/react';
import { ChevronDown, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

/** Listing paths that accept city/location filter params. */
const LISTING_PATHS = new Set(['/', '/male', '/trans']);

function getCurrentCity(): string {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('city') ?? '';
}

function getListingPath(): string {
    if (typeof window === 'undefined') return '/';
    return LISTING_PATHS.has(window.location.pathname)
        ? window.location.pathname
        : '/';
}

export function CitySwitcher({ className }: { className?: string }) {
    const brand = useBrand();
    const [city, setCity] = useState(getCurrentCity);

    // Keep city label in sync when Inertia navigates.
    useEffect(() => {
        setCity(getCurrentCity());
    });

    // Hyperlocal brand: we're already scoped to one city — no switcher needed.
    if (brand.is_hyperlocal) return null;

    function switchCity(next: string) {
        const params: Record<string, string> = {};
        if (next) params.city = next;
        router.visit(getListingPath(), {
            data: params,
            preserveState: false,
            replace: false,
        });
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className={className ?? "hidden gap-1.5 text-muted-foreground hover:text-foreground md:flex"}
                >
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span className="max-w-[120px] truncate text-xs font-medium">
                        {city || 'All Cities'}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem
                    onClick={() => switchCity('')}
                    className={!city ? 'font-semibold' : ''}
                >
                    All Cities
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* City list populated once backend exposes available cities in SharedData. */}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
